
import { useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'

interface Compra {
  _id: string
  numero: string
  insumoId: string
  nomeInsumo: string
  quantidade: number
  unidadeMedida: string
  status: 'cotacao' | 'aprovado' | 'pedido_enviado' | 'entregue' | 'cancelado'
  tipoPedido: 'urgente' | 'normal' | 'programado'
  fornecedorSelecionado?: string
  valorTotal: number
  dataCompra: string
  dataEntregaPrevista: string
  obraId?: string
  etapaId?: string
  centroCusto?: string
  cotacoes: Cotacao[]
  anexos: Anexo[]
  observacoes?: string
}

interface Cotacao {
  fornecedorId: string
  nomeFornecedor: string
  precoUnitario: number
  prazoEntrega: number
  condicoesPagamento: string
  observacoes?: string
  selecionado: boolean
}

interface Anexo {
  nome: string
  url: string
  tipo: string
  tamanho: number
}

interface Fornecedor {
  _id: string
  nome: string
  segmento: string
  contato: string
  endereco?: string
  cnpj?: string
  confiabilidade: number
  tempoEntregaMedio: number
  precoMedio: number
  ativo: boolean
  bloqueado: boolean
  motivoBloqueio?: string
  observacoes?: string
}

interface NotificacaoCompra {
  _id: string
  tipo: 'estoque_baixo' | 'insumo_indisponivel' | 'cotacao_pendente' | 'entrega_atrasada'
  titulo: string
  mensagem: string
  insumoId?: string
  compraId?: string
  obraId?: string
  prioridade: 'baixa' | 'media' | 'alta' | 'critica'
  lida: boolean
  dataNotificacao: string
}

export const useCompras = () => {
  const [compras, setCompras] = useState<Compra[]>([])
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  const [notificacoes, setNotificacoes] = useState<NotificacaoCompra[]>([])
  const [loading, setLoading] = useState(false)

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [comprasData, fornecedoresData, notificacoesData] = await Promise.all([
        lumi.entities.compras.list(),
        lumi.entities.fornecedores.list(),
        lumi.entities.notificacoes_compras.list()
      ])

      setCompras(comprasData.list || [])
      setFornecedores(fornecedoresData.list || [])
      setNotificacoes(notificacoesData.list || [])
    } catch (error: any) {
      console.error('Erro ao carregar dados de compras:', error)
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  // Calculate metrics
  const getMetrics = () => {
    const now = new Date()
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1)

    // Current month metrics
    const comprasAtual = compras.filter(c => new Date(c.dataCompra) >= currentMonth)
    const comprasAnterior = compras.filter(c => 
      new Date(c.dataCompra) >= lastMonth && new Date(c.dataCompra) < currentMonth
    )

    const totalEstoque = compras.filter(c => c.status === 'entregue').length
    const totalCotacao = compras.filter(c => c.status === 'cotacao').length
    const totalComprado = compras.filter(c => ['aprovado', 'pedido_enviado', 'entregue'].includes(c.status)).length
    const valorTotal = compras.reduce((total, c) => total + c.valorTotal, 0)

    // Previous month for comparison
    const totalEstoqueAnterior = comprasAnterior.filter(c => c.status === 'entregue').length
    const totalCotacaoAnterior = comprasAnterior.filter(c => c.status === 'cotacao').length
    const totalCompradoAnterior = comprasAnterior.filter(c => ['aprovado', 'pedido_enviado', 'entregue'].includes(c.status)).length
    const valorTotalAnterior = comprasAnterior.reduce((total, c) => total + c.valorTotal, 0)

    return {
      totalEstoque,
      totalCotacao,
      totalComprado,
      valorTotal,
      totalEstoqueAnterior,
      totalCotacaoAnterior,
      totalCompradoAnterior,
      valorTotalAnterior
    }
  }

  // Generate next purchase number
  const generateNextNumber = () => {
    const year = new Date().getFullYear()
    const existingNumbers = compras
      .filter(c => c.numero.includes(year.toString()))
      .map(c => parseInt(c.numero.split('-').pop() || '0'))
    
    const nextNumber = Math.max(0, ...existingNumbers) + 1
    return `COMP-${year}-${nextNumber.toString().padStart(3, '0')}`
  }

  // Sincronizar estoque com Base de Insumos
  const sincronizarEstoque = async (insumoId: string, quantidade: number, tipo: 'entrada' | 'saida', motivo: string) => {
    try {
      // Buscar o insumo atual
      const { list: insumos } = await lumi.entities.insumos.list()
      const insumo = insumos.find((i: any) => i._id === insumoId)
      
      if (!insumo) {
        console.warn('Insumo não encontrado para sincronização:', insumoId)
        return
      }

      if (!insumo.controlaEstoque) {
        console.log('Insumo não controla estoque, sincronização ignorada')
        return
      }

      const quantidadeAnterior = insumo.quantidadeEstoque || 0
      let quantidadeAtual = quantidadeAnterior

      if (tipo === 'entrada') {
        quantidadeAtual = quantidadeAnterior + quantidade
      } else if (tipo === 'saida') {
        quantidadeAtual = Math.max(0, quantidadeAnterior - quantidade)
      }

      // Atualizar estoque do insumo
      await lumi.entities.insumos.update(insumoId, {
        quantidadeEstoque: quantidadeAtual,
        updatedAt: new Date().toISOString()
      })

      // Criar movimentação de estoque
      await lumi.entities.movimentacoes_estoque.create({
        insumoId,
        tipo,
        quantidade,
        quantidadeAnterior,
        quantidadeAtual,
        motivo,
        dataMovimentacao: new Date().toISOString(),
        usuario: 'Sistema Compras',
        creator: 'sistema',
        createdAt: new Date().toISOString()
      })

      console.log(`Estoque sincronizado: ${insumo.nome} - ${tipo} ${quantidade} unidades`)
    } catch (error) {
      console.error('Erro ao sincronizar estoque:', error)
    }
  }

  // Create compra
  const createCompra = async (data: Omit<Compra, '_id' | 'numero'>) => {
    try {
      const compraData = {
        ...data,
        numero: generateNextNumber(),
        valorTotal: Number(data.valorTotal || 0),
        quantidade: Number(data.quantidade),
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.compras.create(compraData)
      await fetchData()
      toast.success('Compra criada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao criar compra:', error)
      toast.error('Erro ao criar compra')
      throw error
    }
  }

  // Update compra
  const updateCompra = async (id: string, data: Partial<Compra>) => {
    try {
      const compraAtual = compras.find(c => c._id === id)
      if (!compraAtual) throw new Error('Compra não encontrada')

      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.compras.update(id, updateData)

      // Se o status mudou para 'entregue', sincronizar estoque
      if (data.status === 'entregue' && compraAtual.status !== 'entregue') {
        await sincronizarEstoque(
          compraAtual.insumoId,
          compraAtual.quantidade,
          'entrada',
          `Entrega da compra ${compraAtual.numero}`
        )
      }

      await fetchData()
      toast.success('Compra atualizada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar compra:', error)
      toast.error('Erro ao atualizar compra')
      throw error
    }
  }

  // Delete compra
  const deleteCompra = async (id: string) => {
    try {
      await lumi.entities.compras.delete(id)
      await fetchData()
      toast.success('Compra excluída com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir compra:', error)
      toast.error('Erro ao excluir compra')
      throw error
    }
  }

  // Approve quotation
  const approveQuotation = async (compraId: string, fornecedorId: string) => {
    try {
      const compra = compras.find(c => c._id === compraId)
      if (!compra) throw new Error('Compra não encontrada')

      const cotacoes = compra.cotacoes.map(c => ({
        ...c,
        selecionado: c.fornecedorId === fornecedorId
      }))

      const cotacaoSelecionada = cotacoes.find(c => c.selecionado)
      if (!cotacaoSelecionada) throw new Error('Cotação não encontrada')

      await updateCompra(compraId, {
        cotacoes,
        fornecedorSelecionado: fornecedorId,
        valorTotal: cotacaoSelecionada.precoUnitario * compra.quantidade,
        status: 'aprovado'
      })

      toast.success('Cotação aprovada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao aprovar cotação:', error)
      toast.error('Erro ao aprovar cotação')
      throw error
    }
  }

  // Create fornecedor
  const createFornecedor = async (data: Omit<Fornecedor, '_id'>) => {
    try {
      const fornecedorData = {
        ...data,
        confiabilidade: Number(data.confiabilidade),
        tempoEntregaMedio: Number(data.tempoEntregaMedio),
        precoMedio: Number(data.precoMedio),
        ativo: Boolean(data.ativo),
        bloqueado: Boolean(data.bloqueado),
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.fornecedores.create(fornecedorData)
      await fetchData()
      toast.success('Fornecedor criado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao criar fornecedor:', error)
      toast.error('Erro ao criar fornecedor')
      throw error
    }
  }

  // Update fornecedor
  const updateFornecedor = async (id: string, data: Partial<Fornecedor>) => {
    try {
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      }

      await lumi.entities.fornecedores.update(id, updateData)
      await fetchData()
      toast.success('Fornecedor atualizado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao atualizar fornecedor:', error)
      toast.error('Erro ao atualizar fornecedor')
      throw error
    }
  }

  // Block/unblock fornecedor
  const toggleBlockFornecedor = async (id: string, motivo?: string) => {
    try {
      const fornecedor = fornecedores.find(f => f._id === id)
      if (!fornecedor) throw new Error('Fornecedor não encontrado')

      await updateFornecedor(id, {
        bloqueado: !fornecedor.bloqueado,
        motivoBloqueio: !fornecedor.bloqueado ? motivo : '',
        ativo: fornecedor.bloqueado // Se desbloqueando, ativar automaticamente
      })

      toast.success(`Fornecedor ${!fornecedor.bloqueado ? 'bloqueado' : 'desbloqueado'} com sucesso!`)
    } catch (error: any) {
      console.error('Erro ao alterar status do fornecedor:', error)
      toast.error('Erro ao alterar status do fornecedor')
      throw error
    }
  }

  // Delete fornecedor
  const deleteFornecedor = async (id: string) => {
    try {
      await lumi.entities.fornecedores.delete(id)
      await fetchData()
      toast.success('Fornecedor excluído com sucesso!')
    } catch (error: any) {
      console.error('Erro ao excluir fornecedor:', error)
      toast.error('Erro ao excluir fornecedor')
      throw error
    }
  }

  // Mark notification as read
  const markNotificationAsRead = async (id: string) => {
    try {
      await lumi.entities.notificacoes_compras.update(id, {
        lida: true
      })
      await fetchData()
    } catch (error: any) {
      console.error('Erro ao marcar notificação como lida:', error)
    }
  }

  // Create notification for low stock
  const createLowStockNotification = async (insumoId: string, nomeInsumo: string, quantidadeAtual: number) => {
    try {
      await lumi.entities.notificacoes_compras.create({
        tipo: 'estoque_baixo',
        titulo: 'Estoque Baixo',
        mensagem: `O estoque de ${nomeInsumo} está abaixo do mínimo (${quantidadeAtual} unidades restantes)`,
        insumoId,
        prioridade: 'alta',
        lida: false,
        dataNotificacao: new Date().toISOString(),
        creator: 'sistema',
        createdAt: new Date().toISOString()
      })
      await fetchData()
    } catch (error: any) {
      console.error('Erro ao criar notificação:', error)
    }
  }

  // Create notification for unavailable material
  const createUnavailableNotification = async (insumoId: string, nomeInsumo: string, obraId: string, nomeObra: string) => {
    try {
      await lumi.entities.notificacoes_compras.create({
        tipo: 'insumo_indisponivel',
        titulo: 'Insumo Indisponível',
        mensagem: `Solicitação de ${nomeInsumo} na obra '${nomeObra}' - Material não disponível em estoque`,
        insumoId,
        obraId,
        prioridade: 'critica',
        lida: false,
        dataNotificacao: new Date().toISOString(),
        creator: 'sistema',
        createdAt: new Date().toISOString()
      })
      await fetchData()
    } catch (error: any) {
      console.error('Erro ao criar notificação:', error)
    }
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    compras,
    fornecedores,
    notificacoes,
    loading,
    metrics: getMetrics(),
    fetchData,
    createCompra,
    updateCompra,
    deleteCompra,
    approveQuotation,
    createFornecedor,
    updateFornecedor,
    toggleBlockFornecedor,
    deleteFornecedor,
    markNotificationAsRead,
    createLowStockNotification,
    createUnavailableNotification,
    sincronizarEstoque
  }
}
